import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";
import { clearUserTokens } from "./userSession";

const AuthContext = createContext(null);

function canAccessApp(rawUser, otpRequired) {
  if (!rawUser) return false;
  if (rawUser.email_verified && rawUser.mobile_verified) return true;
  // OTP disabled by admin — allow once mobile is saved; verify later from profile.
  if (!otpRequired && rawUser.mobile_number) return true;
  return false;
}

/**
 * AuthProvider
 *
 * Two-tier user state:
 *  - `user`         : account allowed into the app. Fully verified, OR
 *                     OTP verification disabled by admin (verify later from profile).
 *  - `pendingUser`  : a half-registered account that still must finish OTP
 *                     (only when OTP is required). Register page resumes from this.
 *
 * `null` on either means "still loading /me on first paint".
 */
export function AuthProvider({ children }) {
  // Raw /me result: null (loading) | false (anonymous) | object
  const [rawUser, setRawUser] = useState(null);
  const [otpVerificationRequired, setOtpVerificationRequired] = useState(true);
  const [otpSettingLoaded, setOtpSettingLoaded] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      if (typeof data.otp_verification_required === "boolean") {
        setOtpVerificationRequired(data.otp_verification_required);
        setOtpSettingLoaded(true);
      }
      setRawUser(data.user || false);
      return data.user;
    } catch {
      setRawUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    // Always load the live OTP toggle so unverified OTP-off accounts can enter
    // even if /me is from an older backend build.
    api
      .get("/api/auth/signup-config")
      .then(({ data }) => {
        if (typeof data.otp_verification_required === "boolean") {
          setOtpVerificationRequired(data.otp_verification_required);
        }
      })
      .catch(() => {})
      .finally(() => setOtpSettingLoaded(true));
  }, []);

  useEffect(() => {
    // Skip consumer /me on admin routes (avoids noisy 401 in console)
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      setRawUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* ignore */
    }
    clearUserTokens();
    setRawUser(false);
  }, []);

  const setUser = useCallback((u, otpFlag) => {
    if (typeof otpFlag === "boolean") {
      setOtpVerificationRequired(otpFlag);
      setOtpSettingLoaded(true);
    }
    setRawUser(u || false);
  }, []);

  // Derive the public-facing user/pendingUser values.
  // Wait for OTP setting so we don't briefly treat OTP-off users as pending.
  let user;
  let pendingUser = null;
  if (rawUser === null || !otpSettingLoaded) {
    user = null;
  } else if (rawUser === false) {
    user = false;
  } else if (canAccessApp(rawUser, otpVerificationRequired)) {
    user = rawUser;
  } else {
    user = false;
    pendingUser = rawUser;
  }

  return (
    <AuthContext.Provider
      value={{ user, pendingUser, otpVerificationRequired, setUser, fetchMe, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
